import { useCallback, useEffect, useRef } from "react";
import { db } from "../lib/db";

const idleCutoffMs = 5 * 60 * 1_000;
const persistenceIntervalMs = 10_000;

export function useActiveReviewTime(localSessionId: string, enabled: boolean) {
  const lastActivityAt = useRef(Date.now());
  const lastTickAt = useRef(Date.now());
  const pending = useRef(Promise.resolve());

  const flush = useCallback(async (includeHidden = false) => {
    const now = Date.now();
    const countableUntil = Math.min(now, lastActivityAt.current + idleCutoffMs);
    const elapsed = enabled && (includeHidden || document.visibilityState === "visible")
      ? Math.max(0, countableUntil - lastTickAt.current)
      : 0;
    lastTickAt.current = now;
    if (elapsed < 1) return;
    pending.current = pending.current.then(async () => {
      const current = await db.activeTime.get(localSessionId);
      await db.activeTime.put({ id: localSessionId, activeDurationMs: (current?.activeDurationMs ?? 0) + elapsed });
    });
    await pending.current;
  }, [enabled, localSessionId]);

  useEffect(() => {
    if (!enabled) return;
    const markActivity = () => { lastActivityAt.current = Date.now(); };
    const onVisibility = () => {
      if (document.visibilityState === "hidden") {
        void flush(true);
        return;
      }
      lastTickAt.current = Date.now();
      markActivity();
    };
    const onPageHide = () => { void flush(true); };
    window.addEventListener("keydown", markActivity);
    window.addEventListener("pointerdown", markActivity);
    window.addEventListener("scroll", markActivity, { passive: true });
    document.addEventListener("visibilitychange", onVisibility);
    window.addEventListener("pagehide", onPageHide);
    const interval = window.setInterval(() => void flush(), persistenceIntervalMs);
    return () => {
      window.clearInterval(interval);
      window.removeEventListener("keydown", markActivity);
      window.removeEventListener("pointerdown", markActivity);
      window.removeEventListener("scroll", markActivity);
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("pagehide", onPageHide);
      void flush(true);
    };
  }, [enabled, flush]);

  return useCallback(async () => {
    await flush();
    const record = await db.activeTime.get(localSessionId);
    return Math.round((record?.activeDurationMs ?? 0) / 1_000);
  }, [flush, localSessionId]);
}
